import { Button, FullScreenSpinner } from 'components/Elements';
import { Notifications } from 'components/Notifications/Notifications';
import { queryClient } from 'lib/react-query';
import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { HelmetProvider } from 'react-helmet-async';
import { CiDesktop } from 'react-icons/ci';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { BrowserRouter as Router } from 'react-router-dom';

const ErrorFallback = ({ error, resetErrorBoundary }) => {
  const errorCode = error?.response?.status || 'Unknown';
  const errorMessage =
    error?.response?.data?.message || 'Unexpected error occurred.';
  const isClientError = errorCode >= 400 && errorCode < 500;
  const isServerError = errorCode >= 500;

  if (errorCode === 401) {
    localStorage.clear();
    window.location.assign(window.location.origin);
    return null; // Ensure the rest of the fallback doesn't render
  }

  return (
    <div
      className="text-red-500 w-screen h-screen flex flex-col justify-center items-center space-y-4"
      role="alert"
    >
      <h2 className="text-2xl font-semibold">Oops! Something went wrong.</h2>
      <div className="bg-gray-100 p-3">
        <div className="">API Response</div>
        <p className="text-lg text-gray-700">
          <strong>Error Code:</strong> {errorCode}
        </p>
        <p className="text-sm text-gray-600">
          <strong>API Message:</strong> {errorMessage}
        </p>
      </div>
      {isClientError && (
        <p className="text-sm text-yellow-500">
          This seems to be a client-side issue. Please check your input or try
          refreshing.
        </p>
      )}
      {isServerError && (
        <p className="text-sm text-red-600">
          A server-side issue occurred. We are working to resolve it. Please try
          again later.
        </p>
      )}
      <div className="flex flex-row items-center space-x-4">
        <Button
          variant="secondary"
          onClick={() => {
            window.location.assign(window.location.origin);
          }}
        >
          Try Again
        </Button>
        <Button
          onClick={() => {
            localStorage.clear();
            window.location.assign(window.location.origin);
          }}
        >
          Refresh & Logout
        </Button>
      </div>
    </div>
  );
};

const DeviceRestriction = () => {
  return (
    <div className="flex flex-col items-center justify-center w-screen h-screen bg-gray-100 text-center">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">
        Admin Panel Access Restricted
      </h1>
      <p className="text-lg text-gray-600 mb-6">
        This admin panel is only accessible on laptops and desktops. Please
        switch to a compatible device to continue.
      </p>
      <CiDesktop className="w-1/2 max-w-md mb-6" />
      <Button
        onClick={() => window.location.reload()}
        className="bg-blue-500 text-white px-6 py-2 rounded-lg shadow"
      >
        Retry
      </Button>
    </div>
  );
};

export const AppProvider = ({ children }) => {
  const [isDesktop, setIsDesktop] = React.useState(false);

  React.useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const isDesktopOrLaptop =
        width >= 1024 &&
        /Windows|Macintosh|Linux/.test(navigator.userAgent) &&
        !/Mobile|Tablet|iPad|iPhone|Android/.test(navigator.userAgent);

      setIsDesktop(isDesktopOrLaptop);
    };

    checkDevice();

    window.addEventListener('resize', checkDevice);

    return () => {
      window.removeEventListener('resize', checkDevice);
    };
  }, []);

  if (!isDesktop) {
    return <DeviceRestriction />;
  }

  return (
    <React.Suspense fallback={<FullScreenSpinner />}>
      <ErrorBoundary
        FallbackComponent={(props) => <ErrorFallback {...props} />}
      >
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            {process.env.REACT_APP_NODE_ENV !== 'test' && (
              <ReactQueryDevtools />
            )}
            <Notifications />
            <>
              <Router>{children}</Router>
            </>
          </QueryClientProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </React.Suspense>
  );
};
